import Select, { PropsValue, SingleValue, Options } from 'react-select';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers';
import { Provider, ProviderService } from '../../../helpers/models';
import { useClients, useProviderServices } from '../../../helpers/api';
import { useAuth } from '../../auth';
import { visibleProviders } from './providerVisibility';
import { Form } from 'react-bootstrap';
import { MenuComponent } from '../../../../_metronic/assets/ts/components';
import { BsChevronDown } from 'react-icons/bs';

type Props = {
  org: Provider | undefined;
  providers: Provider[] | undefined;
  onChange?: (selectedProviders: Provider[] | undefined, allProviders: boolean) => void;
  className?: string;
  classNamePrefix?: string;
  disabled?: boolean;
  placeHolder?: string;
};

const DownChevron = () => (
  <span style={{ color: '#5e6278 !important' }}>
    <svg height={20} width={20} fill="currentColor">
      <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
    </svg>
  </span>
);

const OrgProvidersMultiSelect: FC<Props> = (props) => {
  const { currentUser } = useAuth();
  useEffect(() => {
    MenuComponent.reinitialization();
  }, []);

  function providerSorter(a: Provider, b: Provider): number {
    const nameA = a.name?.toUpperCase() || '';
    const nameB = b.name?.toUpperCase() || '';

    if (a.uuid === currentUser?.provider?.uuid) {
      return -2;
    }
    if (b.uuid === currentUser?.provider?.uuid) {
      return 2;
    }

    if (nameA < nameB) {
      return -1;
    } else if (nameA > nameB) {
      return 1;
    } else {
      return 0;
    }
  }

  const orgProviders = visibleProviders(props.org?.providers).sort(providerSorter);
  const totalProviders = orgProviders.length;

  // Consumers seed the selected set from the UNFILTERED clinic provider list, so
  // it can hold archived providers this control never renders. Counting those
  // against the visible total made the label read "(12/11)", and — worse — made
  // a partial selection padded out by archived entries read "(Any)". Only the
  // DISPLAY count is filtered: the selection handed to onChange deliberately
  // keeps its archived entries so reports still cover their historical rows.
  const visibleUuids = new Set(orgProviders.map((p) => p.uuid));
  const countVisible = (providers: Provider[] | undefined): number =>
    (providers || []).filter((p) => visibleUuids.has(p.uuid)).length;
  const numSelectedProviders = countVisible(props.providers);

  return (
    <div className={`${props.className} multi-select-container`}>
      <div
        className={`${props.classNamePrefix}__control multi-select-control`}
        data-kt-menu-trigger="click"
        data-kt-menu-placement="bottom-start"
      >
        <div
          className={`${props.classNamePrefix}__value-container ${props.classNamePrefix}__value-container--has-value multi-select-value-container`}
        >
          <div
            className={`${props.classNamePrefix}__single-value multi-select-single-value text-nowrap`}
            style={{ width: '150px' }}
          >
            Providers{' '}
            {totalProviders === numSelectedProviders ? (
              '(Any)'
            ) : numSelectedProviders ? (
              `(${numSelectedProviders}/${totalProviders})`
            ) : (
              <span className="text-danger">(None)</span>
            )}
          </div>
        </div>
        <div className={`${props.classNamePrefix}__indicators multi-select-indicators-container`}>
          <span className={`${props.classNamePrefix}__indicator-separator multi-select-indicator-separator`}></span>
          <div
            className={`${props.classNamePrefix}__indicator ${props.classNamePrefix}__dropdown-indicator multi-select-indicator-container`}
            aria-hidden="true"
          >
            <DownChevron />
          </div>
        </div>
      </div>
      <div
        className={`${props.classNamePrefix}__menu  multi-select-menu menu menu-sub menu-sub-dropdown menu-column menu-gray-600 menu-state-bg-light-primary w-auto fw-bold fs-7 py-5`}
        style={{ overflowY: 'auto', maxHeight: '500px' }}
        data-kt-menu="true"
      >
        <div className="d-flex">
          <h6 className="me-auto ms-10 text-gray-600">Providers</h6>
          <h6
            onClick={() => {
              props.onChange && props.onChange([], false);
            }}
            className="me-5 text-primary pb-1 cursor-pointer fw-lighter"
          >
            Clear
          </h6>
          <h6
            onClick={() => {
              props.onChange && props.onChange(orgProviders || [], true);
            }}
            className="me-10 text-primary pb-1 cursor-pointer fw-lighter"
          >
            All
          </h6>
        </div>
        {orgProviders?.map((s) => (
          <div key={s.uuid} className="mx-10 my-3 d-flex justify-content-between">
            <Form.Check
              label={s.name}
              name="provider-multi-select"
              type="checkbox"
              id={`providers-${s.uuid}`}
              className="my-0 multi-select-input"
              checked={props.providers && props.providers.find((p) => p.uuid === s.uuid) ? true : false}
              onChange={(e) => {
                if (e.target.checked) {
                  const newProviders = [...(props.providers || []), s];
                  // Same visible-only rule as the label: an archived entry riding
                  // along in the selection must not be mistaken for a selected one.
                  const isAllProviders = countVisible(newProviders) === totalProviders;
                  props.onChange && props.onChange(newProviders, isAllProviders);
                } else {
                  props.onChange &&
                    props.onChange(
                      (props.providers || []).filter((p) => p.uuid !== s.uuid),
                      false
                    );
                }
              }}
            />
            <div
              className="text-gray-400 text-hover-primary cursor-pointer ms-4"
              onClick={() => {
                props.onChange && props.onChange([s], false);
              }}
            >
              only
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export { OrgProvidersMultiSelect, DownChevron };
